export interface PhoneCountry {
  iso2: string;
  name: string;
  dial: string;
  flag: string;
  example: string;
  nationalLength: { min: number; max: number };
}

export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { iso2: 'FR', name: 'France', dial: '33', flag: '🇫🇷', example: '6 12 34 56 78', nationalLength: { min: 9, max: 9 } },
  { iso2: 'BE', name: 'Belgique', dial: '32', flag: '🇧🇪', example: '470 12 34 56', nationalLength: { min: 8, max: 9 } },
  { iso2: 'CH', name: 'Suisse', dial: '41', flag: '🇨🇭', example: '79 123 45 67', nationalLength: { min: 9, max: 9 } },
  { iso2: 'LU', name: 'Luxembourg', dial: '352', flag: '🇱🇺', example: '621 123 456', nationalLength: { min: 8, max: 9 } },
  { iso2: 'MC', name: 'Monaco', dial: '377', flag: '🇲🇨', example: '99 99 99 99', nationalLength: { min: 8, max: 8 } },
  { iso2: 'DE', name: 'Allemagne', dial: '49', flag: '🇩🇪', example: '1512 3456789', nationalLength: { min: 10, max: 11 } },
  { iso2: 'ES', name: 'Espagne', dial: '34', flag: '🇪🇸', example: '612 34 56 78', nationalLength: { min: 9, max: 9 } },
  { iso2: 'IT', name: 'Italie', dial: '39', flag: '🇮🇹', example: '312 345 6789', nationalLength: { min: 9, max: 11 } },
  { iso2: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹', example: '912 345 678', nationalLength: { min: 9, max: 9 } },
  { iso2: 'NL', name: 'Pays-Bas', dial: '31', flag: '🇳🇱', example: '6 12345678', nationalLength: { min: 9, max: 9 } },
  { iso2: 'GB', name: 'Royaume-Uni', dial: '44', flag: '🇬🇧', example: '7400 123456', nationalLength: { min: 10, max: 10 } },
  { iso2: 'IE', name: 'Irlande', dial: '353', flag: '🇮🇪', example: '85 123 4567', nationalLength: { min: 9, max: 9 } },
  { iso2: 'US', name: 'États-Unis', dial: '1', flag: '🇺🇸', example: '202 555 0123', nationalLength: { min: 10, max: 10 } },
  { iso2: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦', example: '416 555 0123', nationalLength: { min: 10, max: 10 } },
  { iso2: 'MA', name: 'Maroc', dial: '212', flag: '🇲🇦', example: '612 345 678', nationalLength: { min: 9, max: 9 } },
  { iso2: 'DZ', name: 'Algérie', dial: '213', flag: '🇩🇿', example: '551 23 45 67', nationalLength: { min: 9, max: 9 } },
  { iso2: 'TN', name: 'Tunisie', dial: '216', flag: '🇹🇳', example: '20 123 456', nationalLength: { min: 8, max: 8 } },
  { iso2: 'SN', name: 'Sénégal', dial: '221', flag: '🇸🇳', example: '77 123 45 67', nationalLength: { min: 9, max: 9 } },
  { iso2: 'CI', name: "Côte d'Ivoire", dial: '225', flag: '🇨🇮', example: '01 23 45 67 89', nationalLength: { min: 10, max: 10 } },
  { iso2: 'CM', name: 'Cameroun', dial: '237', flag: '🇨🇲', example: '6 71 23 45 67', nationalLength: { min: 9, max: 9 } },
  { iso2: 'RE', name: 'La Réunion', dial: '262', flag: '🇷🇪', example: '692 12 34 56', nationalLength: { min: 9, max: 9 } },
  { iso2: 'GP', name: 'Guadeloupe', dial: '590', flag: '🇬🇵', example: '690 12 34 56', nationalLength: { min: 9, max: 9 } },
  { iso2: 'MQ', name: 'Martinique', dial: '596', flag: '🇲🇶', example: '696 12 34 56', nationalLength: { min: 9, max: 9 } },
  { iso2: 'GF', name: 'Guyane française', dial: '594', flag: '🇬🇫', example: '694 12 34 56', nationalLength: { min: 9, max: 9 } },
  { iso2: 'YT', name: 'Mayotte', dial: '262', flag: '🇾🇹', example: '639 12 34 56', nationalLength: { min: 9, max: 9 } },
  { iso2: 'NC', name: 'Nouvelle-Calédonie', dial: '687', flag: '🇳🇨', example: '75 12 34', nationalLength: { min: 6, max: 6 } },
  { iso2: 'PF', name: 'Polynésie française', dial: '689', flag: '🇵🇫', example: '87 12 34 56', nationalLength: { min: 8, max: 8 } },
] as const;

export const DEFAULT_PHONE_COUNTRY: PhoneCountry = PHONE_COUNTRIES[0]!;

export function findPhoneCountry(iso2: string): PhoneCountry | undefined {
  return PHONE_COUNTRIES.find((c) => c.iso2 === iso2);
}

export function findPhoneCountryByDial(dial: string): PhoneCountry | undefined {
  const cleaned = dial.replace(/^\+/, '').trim();
  return PHONE_COUNTRIES.find((c) => c.dial === cleaned);
}

/**
 * Construit le format E.164 depuis un indicatif (sans `+`) et un numéro national (digits only).
 * Ex : toE164('33', '612345678') → '+33612345678'
 */
export function toE164(dial: string, national: string): string {
  const d = dial.replace(/\D/g, '');
  const n = national.replace(/\D/g, '').replace(/^0+/, '');
  return `+${d}${n}`;
}

/**
 * Vérifie qu'un numéro respecte la longueur attendue pour son pays.
 */
export function isValidNationalNumber(country: PhoneCountry, national: string): boolean {
  const digits = national.replace(/\D/g, '').replace(/^0+/, '');
  return digits.length >= country.nationalLength.min && digits.length <= country.nationalLength.max;
}
